import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {IFileDocument} from '../../../../models/shared.model';
import {UploadHelper} from '../../../../helper/uploadHelper';
import { AddressHelper } from '../../../../helper/address.helper';
import { MatDialog } from '@angular/material/dialog';
import { ActionHelper } from '../../../../helper/action.helper';
import { Subscription } from 'rxjs';
import { TranslationDirective } from '../../directives/translation.directive';

@Component({
  selector: 'fi-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
  standalone: false,
})
export class EditorComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('editor') editor: ElementRef;
  @ViewChild('info') info: ElementRef;
  @Input() sending: boolean;
  @Input() noUploads: boolean;
  @Input() noBorder: boolean;
  @Input() hideSendButton: boolean;
  @Input() isInvalid: boolean;
  @Input() placeholderTranslation = 'ACTION_GENERAL_HEADLINE_REQUEST_NOTE';
  @Input() infoTooltipTranslation: string;

  @Output() textChanged = new EventEmitter<string>();
  @Output() filesUploaded = new EventEmitter<IFileDocument[]>();
  @Output() commentSubmitted = new EventEmitter<void>();
  @Output() isLongDescription = new EventEmitter<boolean>();

  uploads: IFileDocument[] = [];
  textareaFocused: boolean;
  charactersLengthErrorMessage = false;

  files: IFileDocument[] = [];
  isFileOver: boolean;
  hasInputValue =  false;
  imageUploaded =  false;
  accept = UploadHelper.getGeneralAcceptAttr();

  infoPopupOpened = false;
  infoHovered = false;
  infoPopupBelow = false;
  lastPointerType: string;
  private readonly isDesktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  private readonly INFO_POPUP_WIDTH = 300;
  private readonly INFO_POPUP_GAP = 12;
  private readonly PAGE_GUTTER = 16;
  // Tallest the textarea grows with its content before it scrolls.
  private readonly EDITOR_MAX_HEIGHT = 217;

  // "Upload file (max. 20 MB)" is one translation; Figma shows the link and the
  // bracketed size hint in different styles, so it is split at the bracket.
  uploadLabel = '';
  uploadHint = '';
  private translationSubscription: Subscription;

  constructor(private matDialog: MatDialog) {
  }

  ngOnInit(): void {
    this.translationSubscription = TranslationDirective.getTranslations().subscribe(translations => {
      const text = translations['REQUEST_DETAIL_NOTE_UPLOAD'] ?? '';
      const hintStart = text.lastIndexOf('(');

      this.uploadLabel = hintStart > 0 ? text.slice(0, hintStart).trim() : text;
      this.uploadHint = hintStart > 0 ? text.slice(hintStart).trim() : '';
    });
  }

  ngOnDestroy(): void {
    this.translationSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.sending?.currentValue === false) {
      const editor = this.editor?.nativeElement as HTMLTextAreaElement;
      this.uploads = [];
      this.filesUploaded.emit(this.uploads);
      this.imageUploaded = false;

      if (editor) {
        editor.value = '';
        this.resizeEditor(editor);
        this.textChanged.emit(editor.value);
        this.hasInputValue = false;
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  handleWindowResize(event: Event): void {
    const editor = this.editor?.nativeElement;

    if (editor) {
      this.resizeEditor(editor);
    }

    this.infoPopupOpened = false;
    this.infoHovered = false;

    if (this.infoPopupShown) {
      this.updateInfoPopupPosition();
    }
  }

  // On desktop the popup is also shown while the textarea is focused
  get infoPopupShown(): boolean {
    return this.infoPopupOpened || this.infoHovered || (this.isDesktop && this.textareaFocused);
  }

  onTextareaFocus(): void {
    this.textareaFocused = true;

    if (this.isDesktop && this.infoTooltipTranslation) {
      this.updateInfoPopupPosition();
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: Event): void {
    if (this.infoPopupOpened && !this.info?.nativeElement.contains(event.target)) {
      this.infoPopupOpened = false;
    }
  }

  onInfoPointerEnter(event: PointerEvent): void {
    if (event.pointerType === 'mouse') {
      this.updateInfoPopupPosition();
      this.infoHovered = true;
    }
  }

  onInfoPointerLeave(event: PointerEvent): void {
    if (event.pointerType === 'mouse') {
      this.infoHovered = false;
    }
  }

  onInfoClick(): void {
    // Mouse users get the popup on hover, so a click only toggles it on touch/pen devices
    if (this.lastPointerType === 'mouse') {
      return;
    }

    if (this.infoPopupOpened) {
      this.infoPopupOpened = false;
    } else {
      this.updateInfoPopupPosition(true);
      this.infoPopupOpened = true;
    }
  }

  // Places the popup next to the icon, or below it when forced (mobile) or when there is not enough space beside it
  private updateInfoPopupPosition(forceBelow = false): void {
    const iconRect = (this.info.nativeElement as HTMLElement).getBoundingClientRect();
    const spaceBeside = window.innerWidth - iconRect.right - this.INFO_POPUP_GAP - this.PAGE_GUTTER;

    this.infoPopupBelow = forceBelow || spaceBeside < this.INFO_POPUP_WIDTH;
  }

  onInput(event: Event): void {
    ActionHelper.notifyAboutEdit();
    const editor = event.target as HTMLTextAreaElement;
    this.resizeEditor(editor);
    this.hasInputValue = editor.value.length > 0;

    if (editor.value.length >= 2000) {
      this.charactersLengthErrorMessage = true;
    } else {
      this.charactersLengthErrorMessage = false;
      this.textChanged.emit(editor.value);
    }
    this.isLongDescription.emit(this.charactersLengthErrorMessage);
  }

  createComment(): void {
    this.commentSubmitted.emit();
  }

  onUpload(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    for (let i = 0; i < inputEl.files.length; i++) {
      if (!UploadHelper.isSupportedGeneralFileType(inputEl.files[i])) {
        continue;
      }

      ActionHelper.notifyAboutEdit();

      UploadHelper.uploadFile(inputEl.files[i])
        .then(res => {
          this.uploads.push(res);
          this.imageUploaded = this.uploads.length > 0;
          this.filesUploaded.emit(this.uploads);
          inputEl.value = '';
        })
        .catch(err => {
          inputEl.value = '';
        });

    }

    UploadHelper.showFileTypeErrorIfNecessary(Object.values(inputEl.files), this.matDialog);
  }

  onFileDropped(files: File[]): void {
    if (this.noUploads) {
      return;
    }

    for (const file of files) {
      if (!UploadHelper.isSupportedGeneralFileType(file)) {
        continue;
      }

      ActionHelper.notifyAboutEdit();

      UploadHelper.uploadFile(file)
        .then(res => {
          this.uploads.push(res);
          this.imageUploaded = this.uploads.length > 0;
          this.filesUploaded.emit(this.uploads);
          this.isFileOver = false;
        })
        .catch(err => {
          this.isFileOver = true;
        });
    }

    UploadHelper.showFileTypeErrorIfNecessary(files, this.matDialog);
  }

  removeUpload(index: number): void {
    this.uploads.splice(index, 1);
    this.imageUploaded = this.uploads.length > 0;
    this.filesUploaded.emit(this.uploads);
  }

  resizeEditor(editor: HTMLTextAreaElement): void {
    // With height auto the textarea fills the field (flex), so scrollHeight is
    // the larger of that resting height and the content; grow to it up to the cap.
    editor.style.height = 'auto';
    editor.style.height = Math.min(editor.scrollHeight, this.EDITOR_MAX_HEIGHT) + 'px';
  }

  public onDragOver(evt): void {
    evt.preventDefault();
    evt.stopPropagation();
    this.isFileOver = true;
  }

  public onDragLeave(evt): void {
    evt.preventDefault();
    evt.stopPropagation();

    this.isFileOver = false;
  }
}
